/**
 * Route table and the session gate in front of it.
 *
 * The gate lets the app through on a cached identity as well as a live session, which is what
 * allows a cold start with no connection to land on the roll call instead of a sign-in form.
 */

import { type ReactNode } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

import { SessionStatus, useSession } from './auth/session.js';
import { AuthLoadingPage, SignInPage, SignUpPage } from './routes/AuthPages.js';
import { CalendarPage } from './routes/CalendarPage.js';
import { PeoplePage } from './routes/PeoplePage.js';
import { PersonPage } from './routes/PersonPage.js';
import { ProjectsPage } from './routes/ProjectsPage.js';
import { RollCallPage } from './routes/RollCallPage.js';
import { SettingsPage } from './routes/SettingsPage.js';
import { StatsPage } from './routes/StatsPage.js';

/** Renders the app for an active or cached session and redirects to sign-in otherwise. */
function RequireSession(): ReactNode {
  const { status } = useSession();
  const location = useLocation();

  switch (status) {
    case SessionStatus.Active:
    case SessionStatus.Cached:
      return <Outlet />;
    case SessionStatus.Loading:
      return <AuthLoadingPage />;
    case SessionStatus.SignedOut:
      return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />;
  }
}

/** The application's routes. */
export function App(): ReactNode {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/sign-in/*" element={<SignInPage />} />
        <Route path="/sign-up/*" element={<SignUpPage />} />
        <Route element={<RequireSession />}>
          <Route path="/" element={<ProjectsPage />} />
          <Route path="/p/:projectId" element={<RollCallPage />} />
          <Route path="/p/:projectId/calendar" element={<CalendarPage />} />
          <Route path="/p/:projectId/people" element={<PeoplePage />} />
          <Route path="/p/:projectId/people/:personId" element={<PersonPage />} />
          <Route path="/p/:projectId/stats" element={<StatsPage />} />
          <Route path="/p/:projectId/settings" element={<SettingsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
